import { supabase } from '@/integrations/supabase/client';
import { handleError } from '@/utils/handleError';
import { translateOutsideReact } from '@/i18n/translateOutsideReact';

export interface ChartOfAccount {
  accountNumber: string;
  name: string;
  description: string | null;
}

export class ChartOfAccountsApiClient {
  /**
   * Get all chart of accounts (BAS 2025)
   * Returns simplified response with accountNumber, name, and description
   * @returns Array of chart of accounts
   */
  static async getChartOfAccounts(): Promise<ChartOfAccount[]> {
    try {
      const { data, error } = await supabase.functions.invoke('chart-of-accounts-api', {
        body: { action: 'getChartOfAccounts' },
      });

      if (error) throw error;

      return data.accounts;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToFetchChartOfAccounts'), 'getChartOfAccounts');
      throw error;
    }
  }

  /**
   * Get a specific account by account number
   * @param accountNumber The account number
   * @returns Chart of account or null if not found
   */
  static async getAccountByNumber(accountNumber: string): Promise<ChartOfAccount | null> {
    try {
      const { data, error } = await supabase.functions.invoke('chart-of-accounts-api', {
        body: { action: 'getAccountByNumber', accountNumber },
      });

      if (error) throw error;

      return data.account;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToFetchAccount'), 'getAccountByNumber');
      throw error;
    }
  }

  /**
   * Get accounts by type
   * @param accountType The account type (ASSET, LIABILITY, EQUITY, REVENUE, EXPENSE)
   * @returns Array of chart of accounts
   */
  static async getAccountsByType(accountType: string): Promise<ChartOfAccount[]> {
    try {
      const { data, error } = await supabase.functions.invoke('chart-of-accounts-api', {
        body: { action: 'getAccountsByType', accountType },
      });

      if (error) throw error;

      return data.accounts;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToFetchAccountsByType'), 'getAccountsByType');
      throw error;
    }
  }

  /**
   * Get accounts by statement type
   * @param statementType The statement type (BALANCE_SHEET, INCOME_STATEMENT)
   * @returns Array of chart of accounts
   */
  static async getAccountsByStatementType(statementType: string): Promise<ChartOfAccount[]> {
    try {
      const { data, error } = await supabase.functions.invoke('chart-of-accounts-api', {
        body: { action: 'getAccountsByStatementType', statementType },
      });

      if (error) throw error;

      return data.accounts;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToFetchAccountsByStatementType'), 'getAccountsByStatementType');
      throw error;
    }
  }
}
